import {
  alpha,
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  Popover,
  Tooltip,
  Typography,
  Button
} from '@mui/material';
import { useRef, useState, useEffect } from 'react';
import NotificationsActiveTwoToneIcon from '@mui/icons-material/NotificationsActiveTwoTone';
import DoneAllTwoToneIcon from '@mui/icons-material/DoneAllTwoTone';
import { styled } from '@mui/material/styles';
import { formatDistance } from 'date-fns';
import { tr, enUS, de, fr, es, pt, ko, zhCN, ja } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

import api from 'src/services/api';

const localeMap: Record<string, any> = {
  tr,
  en: enUS,
  de,
  fr,
  es,
  pt,
  kr: ko,
  cn: zhCN,
  jp: ja
};

const NotificationsBadge = styled(Badge)(
  ({ theme }) => `
    .MuiBadge-badge {
        background-color: ${alpha(theme.palette.error.main, 0.1)};
        color: ${theme.palette.error.main};
        min-width: 16px; 
        height: 16px;
        padding: 0;

        &::after {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border-radius: 50%;
            box-shadow: 0 0 0 1px ${alpha(theme.palette.error.main, 0.3)};
            content: "";
        }
    }
`
);

interface NotificationData {
  id: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

function HeaderNotifications() {
  const { t, i18n } = useTranslation();
  const currentLocale = localeMap[i18n.language] || enUS;
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  
  const skipRef = useRef<number>(0);
  const hasMoreRef = useRef<boolean>(true);
  const isLoadingRef = useRef<boolean>(false);
  const limit = 10;

  const fetchNotifications = async (isLoadMore = false) => {
    if (isLoadingRef.current) return;
    if (isLoadMore && !hasMoreRef.current) return;

    isLoadingRef.current = true;
    try {
      const currentSkip = isLoadMore ? skipRef.current : 0;
      const response = await api.get(`/notifications?skip=${currentSkip}&limit=${limit}`);
      const newNotifications = response.data;

      if (isLoadMore) {
        setNotifications((prev) => {
          const existingIds = new Set(prev.map(n => n.id));
          const uniqueNew = newNotifications.filter((n: any) => !existingIds.has(n.id));
          return [...prev, ...uniqueNew];
        });
      } else {
        setNotifications(newNotifications);
      }

      skipRef.current = currentSkip + newNotifications.length;
      hasMoreRef.current = newNotifications.length === limit;
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      isLoadingRef.current = false;
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Poll every minute for the first page
    const interval = setInterval(() => fetchNotifications(false), 60000);
    return () => clearInterval(interval);
  }, []);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const unread = notifications.filter(n => !n.is_read);
      await Promise.all(unread.map(n => api.post(`/notifications/${n.id}/read`)));
      
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true }))
      );
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight * 1.2) {
      fetchNotifications(true);
    }
  };

  return (
    <>
      <Tooltip arrow title={t('common.notifications', 'Bildirimler')}>
        <IconButton color="primary" ref={ref} onClick={handleOpen}>
          <NotificationsBadge
            badgeContent={unreadCount}
            invisible={unreadCount === 0}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            <NotificationsActiveTwoToneIcon />
          </NotificationsBadge>
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '80vh',
            overflow: 'hidden'
          }
        }}
      >
        <Box
          sx={{ p: 2, minWidth: 350, flexShrink: 0 }}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h5">{t('common.notifications', 'Bildirimler')}</Typography>
          {unreadCount > 0 && (
            <Tooltip title={t('common.mark_all_read', 'Tümünü okundu olarak işaretle')} arrow>
              <IconButton color="primary" onClick={handleMarkAllAsRead} size="small">
                <DoneAllTwoToneIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Divider />
        <List 
          sx={{ p: 0, maxHeight: '400px', overflowY: 'auto', overflowX: 'hidden' }}
          onScroll={handleScroll}
        >
          {notifications.length === 0 ? (
            <ListItem sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('common.no_notifications', 'Şu an için yeni bir bildiriminiz bulunmuyor.')}
              </Typography>
            </ListItem>
          ) : (
            notifications.map((notification) => (
              <ListItem
                key={notification.id}
                sx={{
                  p: 2,
                  display: { xs: 'block', sm: 'flex' },
                  bgcolor: notification.is_read ? 'transparent' : 'action.hover',
                  cursor: 'pointer'
                }}
                onClick={() => handleMarkAsRead(notification.id)}
              >
                <Box flex="1">
                  <Box display="flex" justifyContent="space-between">
                    <Typography sx={{ fontWeight: notification.is_read ? 'normal' : 'bold' }}>
                      {notification.title}
                    </Typography>
                    <Typography variant="caption" sx={{ textTransform: 'none' }}>
                      {formatDistance(new Date(notification.created_at), new Date(), {
                        addSuffix: true,
                        locale: currentLocale
                      })}
                    </Typography>
                  </Box>
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    {notification.message}
                  </Typography>
                </Box>
              </ListItem>
            ))
          )}
        </List>
      </Popover>
    </>
  );
}

export default HeaderNotifications;
